import { useEffect, useState, type ReactNode } from "react";
import { tEnglish as t, type PhraseKey } from "@buza/i18n";
import type { ZdSession } from "../api.js";
import { TenantBar, TenantProvider } from "./tenant.js";

// The ZD console layout (S-062, BD-61): header with the signed-in user, navigation between the ZD
// screens, and the tenant bar, the same on every screen (ENT-06). Screens are chosen by the
// location hash (#/tenants, #/ops), so a reload or a shared link opens the same screen. Z03, Z04,
// Z05, Z07, Z08 and ZA01 to ZA05 add a route here when their stories land.

export type ScreenId = "tenants" | "ops" | "prices" | "invoices";

export interface ScreenDef {
  id: ScreenId;
  label: PhraseKey;
  render: () => ReactNode;
}

export function screenFromHash(hash: string, screens: readonly ScreenDef[]): ScreenId {
  const id = hash.replace(/^#\/?/, "").split(/[/?]/)[0];
  return screens.find((s) => s.id === id)?.id ?? screens[0]!.id;
}

export function ConsoleShell({ session, screens, onSignOut }: { session: ZdSession; screens: readonly ScreenDef[]; onSignOut: () => void }) {
  const [current, setCurrent] = useState<ScreenId>(() => screenFromHash(window.location.hash, screens));

  useEffect(() => {
    const onHash = () => setCurrent(screenFromHash(window.location.hash, screens));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [screens]);

  const screen = screens.find((s) => s.id === current)!;
  return (
    <TenantProvider>
      <div className="console">
        <header>
          <h1>{t("console.shell.app_name")}</h1>
          <p>
            {t("console.signin.signed_in_as")} <strong>{session.name}</strong>
          </p>
          <p className="role">{session.role === "super_admin" ? t("console.signin.role_super_admin") : t("console.signin.role_support")}</p>
          <button type="button" onClick={onSignOut}>
            {t("console.signin.sign_out")}
          </button>
        </header>
        <nav aria-label={t("console.common.nav_label")}>
          <ul>
            {screens.map((s) => (
              <li key={s.id}>
                <a
                  href={`#/${s.id}`}
                  aria-current={s.id === current ? "page" : undefined}
                  onClick={() => setCurrent(s.id)}
                >
                  {t(s.label)}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <TenantBar />
        <main>{screen.render()}</main>
      </div>
    </TenantProvider>
  );
}
